/**
 * The payee naming mode's save and its shared words (TMMAPP-0163 item 6), for `NamingModeChoice`
 * and the coverage card. Saving is `PATCH /v1/me {payeeNamingMode}` then a `/me` re-read; it renames
 * nothing and never starts a run.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import type { PayeeNamingMode } from '@/api/me';
import { patchPayeeNamingMode } from '@/api/settings';
import { useMeRefetch } from '@/session/meHooks';

/** Where Settings' "Name them with AI now" lands: the card opens the naming dialog once. */
export const NAME_WITH_AI_STATE = { nameWithAi: true } as const;

export function wantsNameWithAi(state: unknown): boolean {
  return typeof state === 'object' && state !== null && 'nameWithAi' in state;
}

/** The sentence that says the switch is saved and nothing was renamed. */
export function switchedCopyKey(mode: PayeeNamingMode) {
  return mode === 'AI_ALL' ? 'naming.mode.switchedAiAll' : 'naming.mode.switchedFreeFirst';
}

/** Saves the mode and re-reads `/me`; resolves `runSuggested`, or null when it failed. */
export function useNamingModeSave() {
  const { t } = useTranslation();
  const refetchMe = useMeRefetch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(mode: PayeeNamingMode): Promise<{ runSuggested: boolean } | null> {
    if (busy) return null;
    setBusy(true);
    setError(null);
    try {
      const { runSuggested } = await patchPayeeNamingMode(mode);
      await refetchMe();
      return { runSuggested };
    } catch (err) {
      setError(errorCopy(t, err, 'automation.errorGeneric'));
      return null;
    } finally {
      setBusy(false);
    }
  }
  return { save, busy, error };
}
